import AddCircleIcon from '@mui/icons-material/AddCircle';
import Link from '@mui/material/Link';
import {styled} from '@mui/material/styles';
import {useQuery} from '@tanstack/react-query';
import {format} from 'date-fns';
import React, {useState} from 'react';
import {useSelector} from 'react-redux';

import CustomSnackbar from '../../components/CustomSnackBar/CustomSnackBar';
import CustomTable from '../../components/CustomTable/CustomTable';
import FilterBar from '../../components/FilterBar/FilterBar';
import ToolBar from '../../components/ToolBar/ToolBar';
import {fetchOutlet} from '../../service/Outlet';
import {formatChannelName, getDefaultFilters} from '../../utils/helper';

import BulkInsert from './BulkInsert';
import OutletDrawer from './OutletDrawer';

const StyledLink = styled(Link)(({theme}) => ({
  fontWeight: 700,
  textDecoration: 'none',
  '&:hover': {
    cursor: 'pointer',
    textDecoration: 'underline',
  },
}));

const Outlet = () => {
  const permissions = useSelector((state) => state.permissions);

  const commonFilters = useSelector((store) => store.commonFilters);

  const {admin: isAdmin, createOutlet: haveCreateOutletPermission} = permissions;

  const [snackbarAlert, setSnackBarAlert] = useState({isOpen: false, message: '', severity: 'warning'});

  const [bulkUploadOverlay, setBulkUploadOverlay] = useState(false);

  const [uploadType, setUploadType] = useState('');

  const [openDrawer, setOpenDrawer] = useState(false);

  const [selectedRow, setSelectedRow] = useState({index: null, row: null});

  const defaultFilters = getDefaultFilters(window?.location?.search, commonFilters);

  const [filters, setFilters] = useState([
    {
      id: 'Virtual Store Code',
      type: 'menu',
      selectType: 'multiple',
      key: 'partnerStoreCode',
      displayValue: defaultFilters?.partnerStoreCode?.displayValue || [],
      value: defaultFilters?.partnerStoreCode?.value || [],
      isApplied:
        Array.isArray(defaultFilters?.partnerStoreCode?.value) && defaultFilters?.partnerStoreCode?.value.length > 0
          ? true
          : false,
      isDeleteable: true,
    },
    {
      id: 'Channel',
      type: 'menu',
      selectType: 'multiple',
      key: 'channelCode',
      displayValue: defaultFilters?.channelCode?.displayValue || [],
      value: defaultFilters?.channelCode?.value || [],
      isApplied:
        Array.isArray(defaultFilters?.channelCode?.value) && defaultFilters?.channelCode?.value.length > 0
          ? true
          : false,
      isDeleteable: true,
    },
    {
      id: 'Brand',
      type: 'menu',
      selectType: 'multiple',
      key: 'partnerBrandCode',
      displayValue: defaultFilters?.partnerBrandCode?.displayValue || [],
      value: defaultFilters?.partnerBrandCode?.value || [],
      isApplied:
        Array.isArray(defaultFilters?.partnerBrandCode?.value) && defaultFilters?.partnerBrandCode?.value.length > 0
          ? true
          : false,
      isDeleteable: true,
    },
    {
      id: 'Store',
      type: 'menu',
      selectType: 'multiple',
      key: 'partnerPhyStoreCode',
      displayValue: defaultFilters?.partnerPhyStoreCode?.displayValue || [],
      value: defaultFilters?.partnerPhyStoreCode?.value || [],
      isApplied:
        Array.isArray(defaultFilters?.partnerPhyStoreCode?.value) &&
        defaultFilters?.partnerPhyStoreCode?.value.length > 0
          ? true
          : false,
      isDeleteable: true,
    },
    {
      id: 'City',
      type: 'menu',
      selectType: 'multiple',
      key: 'city',
      displayValue: defaultFilters?.city?.displayValue || [],
      value: defaultFilters?.city?.value || [],
      isApplied: Array.isArray(defaultFilters?.city?.value) && defaultFilters?.city?.value.length > 0 ? true : false,
      isDeleteable: true,
    },
    {
      id: 'Country',
      type: 'menu',
      selectType: 'multiple',
      key: 'countryCode',
      displayValue: defaultFilters?.countryCode?.displayValue || [],
      value: defaultFilters?.countryCode?.value || [],
      isApplied:
        Array.isArray(defaultFilters?.countryCode?.value) && defaultFilters?.countryCode?.value.length > 0
          ? true
          : false,
      isDeleteable: true,
    },
    {
      id: 'Mapping status',
      type: 'menu',
      selectType: 'multiple',
      key: 'mappingStatus',
      displayValue: defaultFilters?.mappingStatus?.displayValue || [],
      value: defaultFilters?.mappingStatus?.value || [],
      isApplied:
        Array.isArray(defaultFilters?.mappingStatus?.value) && defaultFilters?.mappingStatus?.value.length > 0
          ? true
          : false,
      isDeleteable: true,
    },
  ]);

  const [{pageIndex, pageSize}, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50,
  });

  const pageSizeOptions = [50, 100, 200];
  const dataQuery = useQuery({
    queryKey: ['data-outlet', {pageIndex, pageSize}],
    queryFn: () => fetchOutlet({pageIndex, pageSize, filters}),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const handleDrawerOpen = (index, row) => {
    setOpenDrawer(true);
    setSelectedRow({index, row});
  };

  const handleDrawerClose = () => {
    setOpenDrawer(false);
    setSelectedRow({index: null, row: null});
  };

  const closeSnackBarAlert = () => {
    setSnackBarAlert((prevState) => ({
      ...prevState,
      isOpen: false,
    }));
  };

  const columns = [
    {
      id: 'Virtual Store Code',
      header: 'Virtual Store Code',
      accessorKey: 'partnerStoreCode',
      filterable: true,
      cell: ({row}) => {
        return (
          <StyledLink
            onClick={() => {
              handleDrawerOpen(row.index, row?.original);
            }}
          >
            {row?.original.partnerStoreCode}
          </StyledLink>
        );
      },
    },
    {
      id: 'Channel',
      header: 'Channel',
      filterable: true,
      accessorFn: (row) => formatChannelName(row),
    },
    {
      id: 'Channel Store Code',
      header: 'Channel Store Code',
      filterable: false,
      accessorKey: 'channelStoreCode',
    },
    {
      id: 'Brand',
      header: 'Brand',
      filterable: true,
      accessorFn: (row) => `${row.partnerBrandName} ${row.partnerBrandCode ? `(${row.partnerBrandCode})` : ``}`,
    },
    {
      id: 'Store',
      header: 'Store',
      accessorFn: (row) =>
        `${row.partnerPhyStoreName} ${row.partnerPhyStoreCode ? `(${row.partnerPhyStoreCode})` : ``}`,
      filterable: true,
    },
    {
      id: 'City',
      header: 'City',
      accessorKey: 'city',
      filterable: true,
    },
    {
      id: 'Country',
      header: 'Country',
      accessorKey: 'countryName',
      filterable: true,
    },
    {
      id: 'Mapping status',
      header: 'Mapping status',
      accessorKey: 'mappingStatus',
      filterable: true,
    },
    {
      id: 'Created At',
      header: 'Created At',
      accessorKey: 'createdAt',
      accessorFn: (row) => format(new Date(row.createdAt).getTime(), 'dd MMM yyyy HH:mm:ss'),
      filterable: false,
    },
    {
      id: 'Updated At',
      header: 'Updated At',
      accessorKey: 'updatedAt',
      accessorFn: (row) => format(new Date(row.updatedAt).getTime(), 'dd MMM yyyy HH:mm:ss'),
      filterable: false,
    },
  ];

  const getDataToExport = () => {
    if (Array.isArray(dataQuery.data?.rows) && dataQuery.data.rows.length > 0) {
      const fileName = `outlets`;
      const data = {
        fileName,
        dataToExport: `Virtual Store Code, Channel, Partner Channel Code, Channel Store Code, Brand Code, Brand, Store Code, Store, City, Country, status, createdAt, updatedAt`,
      };
      dataQuery.data?.rows?.forEach(
        ({
          partnerStoreCode,
          channelName,
          channelGroupName,
          partnerChannelCode,
          channelStoreCode,
          partnerBrandCode,
          partnerBrandName,
          partnerPhyStoreCode,
          partnerPhyStoreName,
          city,
          countryName,
          mappingStatus,
          createdAt,
          updatedAt,
        }) => {
          const channel =
            channelGroupName === channelName ? `${channelName}-direct` : `${channelGroupName}-${channelName}`;
          data.dataToExport += `\n${partnerStoreCode},${channel},${partnerChannelCode},${channelStoreCode},${partnerBrandCode},${partnerBrandName},${partnerPhyStoreCode},${partnerPhyStoreName},${city},${countryName},${mappingStatus},${createdAt},${updatedAt}`;
        }
      );
      return data;
    } else {
      return null;
    }
  };

  const handleCreateButton = (selectedOption) => {
    if (selectedOption === 'single') {
      handleDrawerOpen(-1, null);
    } else if (selectedOption === 'bulk' || selectedOption === 'ancillary') {
      setBulkUploadOverlay(true);
      setUploadType(selectedOption);
    }
  };

  const downloadTemplate = (type) => {
    let data = {};
    if (type === 'bulk') {
      data = {
        fileName: `bulkOutletCreationTemplate`,
        dataToExport: `partnerStoreCode,channel,channelStoreCode,channelBrandAuthKey\r\nVST178949082,swiggy-direct,3030_3_3,\r\nVST178949082,urbanpiper-careem,VST178949082-careem,abcdefgh`,
      };
    } else {
      data = {
        fileName: `bulkAncillaryMappingTemplate`,
        dataToExport: `partnerStoreCode,channel,stationCode,cutOffTime,allowedPaymentMode,latitude,longitude\r\nVST123456789,irctc-direct,PUNE,60,cod-credit_sale,10.12345,-90.3424423`,
      };
    }
    const blob = new Blob([data.dataToExport], {type: 'application/json', encoding: 'UTF-8'});
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `${data.fileName}.csv`;
    link.click();
  };

  const createBtnProps = {
    button: isAdmin || haveCreateOutletPermission,
    tooltip: false,
    text: 'Create',
    menuItems: [
      {
        text: 'Outlet Single',
        onClick: () => handleCreateButton('single'),
      },
      {
        text: 'Outlet Bulk',
        onClick: () => handleCreateButton('bulk'),
      },
      {
        text: 'Ancillary Bulk',
        onClick: () => handleCreateButton('ancillary'),
      },
      {
        text: 'Download Outlet Bulk Template',
        onClick: () => downloadTemplate('bulk'),
      },
      {
        text: 'Download Ancillary Bulk Template',
        onClick: () => downloadTemplate('ancillary'),
      },
    ],
    icon: AddCircleIcon,
  };

  return (
    <>
      <ToolBar
        headerText={'Outlets'}
        showGlobalSearch={false}
        showLegends={false}
        showExportButton={{button: true, tooltip: true}}
        getDataToExport={getDataToExport}
        showCustomButtons={[createBtnProps]}
      />
      <FilterBar
        filters={filters}
        setFilters={setFilters}
        showApplyActionButton={true}
        showFilterActionButton={true}
        dataQuery={[dataQuery]}
        allowDateRangeSelection={false}
        setPagination={setPagination}
        setSnackBarAlert={setSnackBarAlert}
        shouldValidateMultiOpsSelection={true}
      />
      <CustomTable
        tableKey={'outlets'}
        columns={columns}
        dataQuery={dataQuery}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        pageIndex={pageIndex}
        setPagination={setPagination}
        filters={filters}
        setFilters={setFilters}
        allowDateRangeSelection={false}
      />
      {openDrawer && (
        <OutletDrawer
          open={openDrawer}
          onToggleOff={handleDrawerClose}
          setSnackBarAlert={setSnackBarAlert}
          selectedRow={selectedRow}
          dataQuery={dataQuery}
        />
      )}
      {snackbarAlert && (
        <CustomSnackbar
          autoHideDuration={snackbarAlert?.severity === 'error' ? 30000 : 5000}
          message={snackbarAlert.message}
          isOpen={snackbarAlert.isOpen}
          showCloseButton={true}
          handleClose={closeSnackBarAlert}
          isAlert={true}
          severity={snackbarAlert?.severity}
          anchorOriginVertical={'bottom'}
        />
      )}
      <BulkInsert
        open={bulkUploadOverlay}
        onClose={() => setBulkUploadOverlay(false)}
        setSnackBarAlert={setSnackBarAlert}
        uploadType={uploadType}
      />
    </>
  );
};

export default Outlet;
